import { parseRoute, routeHash } from "./router.js";
import { state, isDirty, saveWorking } from "./state.js";
import { chooseUnsaved, showMessages } from "./ui/dialogs.js";
import { announce } from "./ui/dom.js";
import * as convertPage from "./pages/convert.js";
import * as resultPage from "./pages/result.js";
import * as profilesPage from "./pages/profiles.js";
import * as profilePage from "./pages/profile.js";
import * as rulePage from "./pages/rule.js";
import * as codePage from "./pages/code.js";
import * as demoPage from "./pages/demo.js";

const PAGES = { convert: convertPage, result: resultPage, profiles: profilesPage, profile: profilePage, rule: rulePage, code: codePage, demo: demoPage };
const EDITING = new Set(["profile", "rule", "code"]);
const root = document.getElementById("page");
let currentHash = null;
let view = {};

const nav = {
  go(hash) {
    if (location.hash === hash) show();
    else location.hash = hash;
  },
  replace(hash) {
    history.replaceState(null, "", hash);
    currentHash = hash;
  },
};

const leavesProfile = (route) => !(EDITING.has(route.name) && state.working && route.id === state.working.id);

// Returns the route to show (possibly updated), or null to stay put.
async function canLeave(route) {
  const editingId = state.working?.id;
  if (view.canLeave && !(await view.canLeave(route))) return null;
  // The page's own guard may have forked a built-in while saving: follow the new id.
  if (editingId && state.working && state.working.id !== editingId && EDITING.has(route.name) && route.id === editingId) {
    route = { ...route, id: state.working.id };
  }
  if (state.working && leavesProfile(route)) {
    if (isDirty()) {
      const choice = await chooseUnsaved({ message: `“${state.working.name}” has changes you haven't saved.`, saveLabel: "Save profile" });
      if (choice === "cancel") {
        view.leaveCancelled?.();
        return null;
      }
      if (choice === "save") {
        try {
          saveWorking();
          announce("Profile saved in this browser.");
        } catch (error) {
          await showMessages("Couldn't save", [error.message]);
          view.leaveCancelled?.();
          return null;
        }
      }
    }
    state.working = null;
    state.saved = null;
    state.ruleDraft = null;
  }
  return route;
}

async function onHashChange() {
  const hash = location.hash || "#/";
  if (hash === currentHash) return;
  const route = await canLeave(parseRoute(hash));
  if (!route) {
    history.replaceState(null, "", currentHash);
    return;
  }
  const target = routeHash(route);
  if (route.id !== undefined && target !== hash) history.replaceState(null, "", target);
  show();
}

function markCurrentNav(route) {
  const section = EDITING.has(route.name) || route.name === "profiles" ? "profiles" : "convert";
  for (const link of document.querySelectorAll(".site-header nav a")) {
    if (link.dataset.section === section) link.setAttribute("aria-current", "page");
    else link.removeAttribute("aria-current");
  }
}

function show() {
  const route = parseRoute(location.hash);
  currentHash = location.hash || "#/";
  root.replaceChildren();
  view = PAGES[route.name].mount(root, route, nav) ?? {};
  document.title = `${view.title ?? "Convert"} · Mammoth Press`;
  markCurrentNav(route);
  window.scrollTo(0, 0);
  const heading = root.querySelector("h1");
  if (heading) {
    heading.tabIndex = -1;
    heading.focus({ preventScroll: true });
  }
}

window.addEventListener("hashchange", onHashChange);
window.addEventListener("beforeunload", (event) => {
  if (isDirty() || view.isDirty?.()) {
    event.preventDefault();
    event.returnValue = "";
  }
});

show();
